import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import LanguageSwitcher from '../../components/common/LanguageSwitcher.jsx';

export default function SettingsPage() {
  const { t }        = useTranslation();
  const { user }     = useAuth();
  const [tab, setTab] = useState('profile');

  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '' });
  const [sms, setSms]          = useState({ phone: '', smsAlerts: false });
  const [twoFAData, setTwoFAData] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [loading, setLoading]  = useState(false);

  async function saveProfile(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch('/users/me', { name: profile.name });
      toast.success(t('common.success'));
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  }

  async function saveSMS(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/notifications/sms-config', sms);
      toast.success(t('common.success'));
    } catch (err) { toast.error(err.response?.data?.message || t('common.error')); }
    finally { setLoading(false); }
  }

  async function testSMS() {
    setLoading(true);
    try {
      const { data } = await api.post('/notifications/sms-test');
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.message || t('common.error')); }
    finally { setLoading(false); }
  }

  async function setup2FA() {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setTwoFAData(data.data);
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  }

  async function enable2FA(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/2fa/enable', { token: twoFACode });
      toast.success('2FA activé');
      setTwoFAData(null);
      setTwoFACode('');
    } catch (err) { toast.error(err.response?.data?.message || t('common.error')); }
    finally { setLoading(false); }
  }

  const tabs = [
    { id: 'profile',       label: t('settings.profile') },
    { id: 'notifications', label: t('settings.notifications') },
    { id: 'security',      label: t('settings.security') },
    { id: 'language',      label: t('settings.language') },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <h1 style={{ color: 'var(--elvyra-text-primary)', marginBottom: '24px', fontSize: '24px' }}>
        {t('settings.title')}
      </h1>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', borderBottom: '1px solid var(--elvyra-border)', paddingBottom: '0' }}>
        {tabs.map(tab_ => (
          <button key={tab_.id} onClick={() => setTab(tab_.id)} style={{
            padding: '10px 16px',
            background: 'none',
            border: 'none',
            borderBottom: tab === tab_.id ? '2px solid var(--elvyra-accent)' : '2px solid transparent',
            color: tab === tab_.id ? 'var(--elvyra-accent)' : 'var(--elvyra-text-secondary)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: tab === tab_.id ? 600 : 400,
            transition: 'all 0.2s',
          }}>
            {tab_.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', color: 'var(--elvyra-text-secondary)', marginBottom: '6px', fontSize: '13px' }}>
              {t('auth.name')}
            </label>
            <input
              value={profile.name}
              onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--elvyra-surface-2)', border: '1px solid var(--elvyra-border)', borderRadius: '8px', color: 'var(--elvyra-text-primary)', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', color: 'var(--elvyra-text-secondary)', marginBottom: '6px', fontSize: '13px' }}>
              {t('auth.email')}
            </label>
            <input
              value={profile.email} disabled
              style={{ width: '100%', padding: '10px 14px', background: 'var(--elvyra-surface-2)', border: '1px solid var(--elvyra-border)', borderRadius: '8px', color: 'var(--elvyra-text-secondary)', fontSize: '14px', boxSizing: 'border-box', opacity: 0.6 }}
            />
          </div>
          <button type="submit" disabled={loading} style={{ alignSelf: 'flex-start', padding: '10px 24px', background: 'var(--elvyra-accent)', color: '#060E1F', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
            {t('common.save')}
          </button>
        </form>
      )}

      {tab === 'notifications' && (
        <form onSubmit={saveSMS} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', color: 'var(--elvyra-text-secondary)', marginBottom: '6px', fontSize: '13px' }}>
              {t('notifications.phone')}
            </label>
            <input
              type="tel"
              value={sms.phone}
              onChange={e => setSms(s => ({ ...s, phone: e.target.value }))}
              placeholder={t('notifications.phonePlaceholder')}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--elvyra-surface-2)', border: '1px solid var(--elvyra-border)', borderRadius: '8px', color: 'var(--elvyra-text-primary)', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: 'var(--elvyra-text-primary)', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={sms.smsAlerts}
              onChange={e => setSms(s => ({ ...s, smsAlerts: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: 'var(--elvyra-accent)' }}
            />
            {t('notifications.smsAlerts')}
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="submit" disabled={loading} style={{ padding: '10px 24px', background: 'var(--elvyra-accent)', color: '#060E1F', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
              {t('notifications.saveConfig')}
            </button>
            <button type="button" onClick={testSMS} disabled={loading} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--elvyra-accent)', borderRadius: '8px', border: '1px solid var(--elvyra-accent)', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
              {t('notifications.testSMS')}
            </button>
          </div>
        </form>
      )}

      {tab === 'security' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ background: 'var(--elvyra-surface-2)', padding: '20px', borderRadius: '12px', border: '1px solid var(--elvyra-border)' }}>
            <h3 style={{ color: 'var(--elvyra-text-primary)', marginBottom: '8px', fontSize: '16px' }}>
              {t('settings.twoFactor')}
            </h3>
            {!twoFAData ? (
              <button onClick={setup2FA} disabled={loading} style={{ padding: '10px 20px', background: 'var(--elvyra-accent)', color: '#060E1F', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
                {t('settings.enable2FA')}
              </button>
            ) : (
              <form onSubmit={enable2FA} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{ color: 'var(--elvyra-text-secondary)', fontSize: '13px' }}>
                  Scannez ce QR code avec Google Authenticator ou Authy :
                </p>
                <code style={{ background: '#000', padding: '8px 12px', borderRadius: '6px', color: 'var(--elvyra-accent)', fontSize: '11px', wordBreak: 'break-all' }}>
                  {twoFAData.otpauthUrl}
                </code>
                <p style={{ color: 'var(--elvyra-text-secondary)', fontSize: '13px' }}>
                  Secret : <strong style={{ color: 'var(--elvyra-text-primary)' }}>{twoFAData.secret}</strong>
                </p>
                <input
                  type="text" maxLength={6} value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value)}
                  placeholder="Code à 6 chiffres"
                  style={{ width: '180px', padding: '10px 14px', background: 'var(--elvyra-surface-2)', border: '1px solid var(--elvyra-border)', borderRadius: '8px', color: 'var(--elvyra-text-primary)', fontSize: '18px', textAlign: 'center', letterSpacing: '4px' }}
                />
                <button type="submit" disabled={loading} style={{ alignSelf: 'flex-start', padding: '10px 20px', background: 'var(--elvyra-accent)', color: '#060E1F', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                  {t('auth.verifyBtn')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {tab === 'language' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ color: 'var(--elvyra-text-secondary)', fontSize: '14px' }}>
            {t('settings.language')}
          </p>
          <LanguageSwitcher />
        </div>
      )}
    </div>
  );
}
